import { useEffect, useMemo, useState } from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  FormControlLabel, Radio, RadioGroup, Switch, TextField, Typography,
} from "@mui/material";
import { useCoTermOptions, coTermOptionsError } from "../../api/useCoTermOptions";
import { QuoteCoTermOption, QuoteTerm } from "../../shared/types";
import { EditorSectionDates, emptySectionDates } from "../QuoteSectionBar/sectionUtils";
import { formatIsoDate, inclusiveDays, isBeforeIso, isIsoDate, pastFirstTermWarning, standardEndIso, todayIso } from "../../shared/quoteDate";
import "./QuoteDatesModal.scss";

type Choice = "standard" | "coterm" | "manual";

export type QuoteDatesModalResult = { dates: EditorSectionDates; inherits: boolean; renewalDate?: string | null };

type Props = {
  open: boolean;
  onClose: () => void;
  scope: "section" | "line";
  quoteType: "customer" | "prospect";
  customerGuid: string | null;
  term: QuoteTerm;
  // Every term in scope (a section can mix them) - decides whether a manual end date past the
  // first year gets a warning. Defaults to just `term`.
  terms?: readonly QuoteTerm[];
  title: string;
  value: EditorSectionDates;
  inheritsSectionDates?: boolean;
  // Line scope only - the renewal date on the underlying subscription (sourced from Microsoft on
  // import). Distinct from start/end: nothing else derives from it, it's purely editable
  // record-keeping, but it lives in the same "Dates" column/dialog so staff have one place for
  // everything date-related on a line instead of two.
  renewalDate?: string | null;
  // Issue 33: the section's current (un-prorated) total, for a rough estimate in the preview.
  estimatedCurrentTotal?: number;
  onConfirm: (result: QuoteDatesModalResult) => void;
};

const money = (value: number) => value.toLocaleString(undefined, { style: "currency", currency: "USD" });

const initialChoice = (value: EditorSectionDates): Choice =>
  value.endDateSource === "coterm" ? "coterm" : value.endDateSource === "manual" ? "manual" : "standard";

const QuoteDatesModal = ({ open, onClose, scope, quoteType, customerGuid, term, terms, title, value, inheritsSectionDates = true, renewalDate = null, estimatedCurrentTotal, onConfirm }: Props) => {
  const coTermOptions = useCoTermOptions();
  const [inherit, setInherit] = useState(scope === "line" ? inheritsSectionDates : false);
  const [startDate, setStartDate] = useState(value.startDate || todayIso());
  const [choice, setChoice] = useState<Choice>(initialChoice(value));
  const [manualEnd, setManualEnd] = useState(value.endDateSource === "manual" ? value.endDate : "");
  const [cotermEnd, setCotermEnd] = useState(value.endDateSource === "coterm" ? value.endDate : "");
  const [options, setOptions] = useState<QuoteCoTermOption[] | null>(null);
  const [advisory, setAdvisory] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [renewalDraft, setRenewalDraft] = useState(renewalDate ? renewalDate.slice(0, 10) : "");

  useEffect(() => {
    if (!open) return;
    setInherit(scope === "line" ? inheritsSectionDates : false);
    setStartDate(value.startDate || todayIso());
    setChoice(initialChoice(value));
    setManualEnd(value.endDateSource === "manual" ? value.endDate : "");
    setCotermEnd(value.endDateSource === "coterm" ? value.endDate : "");
    setOptions(null);
    setLookupError(null);
    setRenewalDraft(renewalDate ? renewalDate.slice(0, 10) : "");
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const canLookUp = quoteType === "customer" && Boolean(customerGuid) && isIsoDate(startDate);
  const runLookup = () => {
    if (!canLookUp) return;
    setLookupError(null);
    coTermOptions.mutate({ customer_guid: customerGuid!, start_date: startDate, term_duration: term }, {
      onSuccess: result => {
        setOptions(result.options);
        setAdvisory(result.has_matching_subscription);
        if (result.recommended_end_date && !cotermEnd) setCotermEnd(result.recommended_end_date);
      },
      onError: error => { setOptions([]); setLookupError(coTermOptionsError(error)); },
    });
  };

  // Fetch co-term options when the customer picks that choice (or the start date changes while on it).
  useEffect(() => {
    if (open && choice === "coterm" && canLookUp && options === null && !coTermOptions.isLoading) runLookup();
  }, [open, choice, startDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const effectiveEnd = useMemo(() => {
    if (quoteType === "prospect") return manualEnd || standardEndIso(startDate, term);
    if (choice === "manual") return manualEnd;
    if (choice === "coterm") return cotermEnd;
    return standardEndIso(startDate, term);
  }, [quoteType, choice, manualEnd, cotermEnd, startDate, term]);

  const billableDays = inclusiveDays(startDate, effectiveEnd);
  const fullTermDays = inclusiveDays(startDate, standardEndIso(startDate, term));
  const endBeforeStart = isIsoDate(effectiveEnd) && !isBeforeIso(startDate, effectiveEnd);
  const manualEndInUse = quoteType === "prospect" || choice === "manual";
  const firstTermWarning = !endBeforeStart && manualEndInUse
    ? pastFirstTermWarning(startDate, manualEnd, "manual", terms ?? [term])
    : null;

  const canConfirm = inherit || (isIsoDate(startDate)
    && (quoteType === "prospect"
      ? (!manualEnd || (isIsoDate(manualEnd) && !endBeforeStart))
      : choice === "standard"
        || (choice === "manual" && isIsoDate(manualEnd) && !endBeforeStart)
        || (choice === "coterm" && isIsoDate(cotermEnd))));

  const confirm = () => {
    // Renewal is line-scope-only and independent of everything else in this dialog (inherit,
    // start/end choice) - always included alongside whichever dates result fires below.
    const renewalResult = scope === "line" ? { renewalDate: renewalDraft || null } : {};
    if (inherit && scope === "line") return onConfirm({ dates: emptySectionDates(), inherits: true, ...renewalResult });
    const base = { ...emptySectionDates(), startDate };
    if (quoteType === "prospect")
      return onConfirm({ dates: { ...base, endDate: manualEnd || "", endDateSource: manualEnd ? "manual" : "" }, inherits: false, ...renewalResult });
    if (choice === "manual")
      return onConfirm({ dates: { ...base, endDate: manualEnd, endDateSource: "manual" }, inherits: false, ...renewalResult });
    if (choice === "coterm") {
      const card = options?.find(option => option.end_date === cotermEnd);
      return onConfirm({ dates: {
        ...base, endDate: cotermEnd, endDateSource: "coterm",
        cotermTargetIds: card ? JSON.stringify(card.subscription_ids) : null,
        cotermTargetLabel: card?.subscription_labels.join(", ") ?? null,
      }, inherits: false, ...renewalResult });
    }
    return onConfirm({ dates: { ...base, endDate: "", endDateSource: "" }, inherits: false, ...renewalResult });
  };

  const disabled = scope === "line" && inherit;

  return <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>{title}</DialogTitle>
    <DialogContent dividers>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        {scope === "line" && <FormControlLabel
          control={<Switch checked={inherit} onChange={event => setInherit(event.target.checked)} />}
          label="Inherit the section dates" />}

        <TextField label="Start date" type="date" size="small" disabled={disabled}
          value={startDate} onChange={event => { setStartDate(event.target.value); setOptions(null); }}
          inputProps={{ min: todayIso() }} InputLabelProps={{ shrink: true }} />

        {!disabled && quoteType === "customer" && <RadioGroup value={choice} onChange={event => setChoice(event.target.value as Choice)}>
          <FormControlLabel value="standard" control={<Radio />} label="Use Standard Terms" />
          {choice === "standard" && advisory && <Alert severity="info" sx={{ ml: 4, mb: 1 }}>
            {scope === "section"
              ? "One or more lines in this section could be aligned to an existing subscription."
              : "This customer already has a subscription with the same term and billing frequency - you could align this to it."}
          </Alert>}
          <FormControlLabel value="coterm" control={<Radio />} label="Align to an existing subscription" />
          {choice === "coterm" && <Box sx={{ ml: 4, mb: 1 }}>
            {coTermOptions.isLoading && <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} /><Typography variant="body2">Loading end dates from Partner Center…</Typography>
            </Box>}
            {lookupError && <Alert severity="error" action={<Button color="inherit" size="small" startIcon={<ContentCopyIcon />}
              onClick={() => navigator.clipboard.writeText(lookupError)}>Copy Error</Button>}>
              <Typography component="pre" sx={{ whiteSpace: "pre-wrap", fontSize: 12 }}>{lookupError}</Typography>
              <Button size="small" onClick={runLookup}>Retry</Button>
            </Alert>}
            {options?.length === 0 && !lookupError && !coTermOptions.isLoading &&
              <Typography variant="body2" color="text.secondary">Microsoft offers no alignment dates for this line - use the standard term or enter a date manually.</Typography>}
            {/* A NESTED RadioGroup, not plain FormControlLabels - these date options were
                previously direct children of the outer choice RadioGroup ("Standard term" /
                "Align to..." / "Enter manually"), and MUI's RadioGroup chains its onChange onto
                every descendant Radio regardless of nesting depth. Since these inner radios have
                no `value` of their own on their native input, that stray onChange fired with the
                browser's default value "on", corrupting the outer `choice` state - which is
                exactly the reported bug: the option list would vanish (choice no longer matched
                "coterm"), every top-level radio appeared unchecked, and the section's dates could
                never actually be confirmed. A nested RadioGroup has its own context, scoped to
                just this list, so it no longer touches the outer one at all. */}
            {options && options.length > 0 && <RadioGroup value={cotermEnd} onChange={event => setCotermEnd(event.target.value)}
              sx={{ maxHeight: 280, overflowY: "auto" }}>
              {options.map(option => <Box key={option.end_date} sx={{ mb: 1 }}>
                <FormControlLabel value={option.end_date}
                  control={<Radio />}
                  label={<Box>
                    <Typography variant="body2">
                      {formatIsoDate(option.end_date)}
                      {options.length === 1 && <Chip size="small" label="Recommended" color="primary" sx={{ ml: 1 }} />}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.type === "calendar_month" ? "Calendar-month aligned"
                        : `${option.count} subscription${option.count === 1 ? "" : "s"}: ${option.subscription_labels.join(", ")}`}
                    </Typography>
                  </Box>} />
              </Box>)}
            </RadioGroup>}
          </Box>}
          <FormControlLabel value="manual" control={<Radio />} label="Enter an end date manually" />
        </RadioGroup>}

        {!disabled && (quoteType === "prospect" || choice === "manual") && <TextField
          label={quoteType === "prospect" ? "End date (optional - blank means the standard term)" : "End date"}
          type="date" size="small" value={manualEnd} onChange={event => setManualEnd(event.target.value)}
          inputProps={{ min: startDate }} InputLabelProps={{ shrink: true }} />}

        {!disabled && isIsoDate(startDate) && (endBeforeStart || (isIsoDate(effectiveEnd) && billableDays > 0)) &&
          <Alert severity={endBeforeStart ? "error" : "info"}>
            {endBeforeStart
              ? "The end date must be after the start date."
              : <>
                Billable period: {formatIsoDate(startDate)} - {formatIsoDate(effectiveEnd)}, {billableDays} of {fullTermDays} days
                {/* Past a full term the server never bills more than one term (P1M is cut to its
                    first calendar month, an annual line is priced as one full year), so a percent
                    over 100 or a scaled-up estimate would contradict what actually gets priced. */}
                {fullTermDays > 0 && billableDays <= fullTermDays && ` (~${Math.round((billableDays / fullTermDays) * 100)}% of a full term)`}.
                {estimatedCurrentTotal != null && estimatedCurrentTotal > 0 && fullTermDays > 0 && billableDays <= fullTermDays &&
                  ` Estimated section total ≈ ${money((estimatedCurrentTotal * billableDays) / fullTermDays)}.`}
              </>}
          </Alert>}

        {!disabled && firstTermWarning && <Alert severity="warning">{firstTermWarning}</Alert>}

        {scope === "line" && <>
          <Divider />
          <Box>
            <Typography variant="subtitle2">Renewal</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}>
              <TextField label="Renewal date" type="date" size="small" value={renewalDraft}
                onChange={event => setRenewalDraft(event.target.value)} InputLabelProps={{ shrink: true }} />
              {renewalDraft && <Button size="small" color="inherit" onClick={() => setRenewalDraft("")}>Clear</Button>}
            </Box>
          </Box>
        </>}
      </Box>
    </DialogContent>
    <DialogActions className="quote-dates-dialog-actions">
      <Button variant="outlined" onClick={onClose}>Cancel</Button>
      <Button variant="contained" disabled={!canConfirm} onClick={confirm}>Confirm</Button>
    </DialogActions>
  </Dialog>;
};

export default QuoteDatesModal;
